import { isFlightRecorderEnabled, recordFlightEvent } from '../debug/flightRecorder'
import { getCurrentFlowSpanId } from '../debug/flightCorrelation'

function emitSeatOpt (milestone, meta = {}) {
  if (!isFlightRecorderEnabled()) return
  recordFlightEvent(
    `SEAT:OPT_${String(milestone || 'UNKNOWN').toUpperCase()}`,
    {
      flowSpanId: getCurrentFlowSpanId() || undefined,
      ...meta,
    },
    'seatOptimisticSelect',
  )
}

/**
 * 404 without fake client → continue WS-only (API may not have /block yet).
 * @param {any} error
 * @param {boolean} fakeSeatBlockEnabled
 */
export function isIgnorableBlock404 (error, fakeSeatBlockEnabled) {
  return (
    error?.response?.status === 404 &&
    !fakeSeatBlockEnabled
  )
}

/**
 * Seat tap: WS seat:selected + passenger modal only. No HTTP /block here —
 * the single block runs on Continuar with fareId + kiosk-generated syncEntryId.
 *
 * Event order: ws_selected → modal_open
 *
 * @param {object} deps
 * @param {ReturnType<import('./seatLockTracker').createSeatLockTracker>} deps.tracker
 * @param {object} deps.seat
 * @param {() => void} deps.pushWsSelected
 * @param {() => void} deps.openModal
 * @param {(name: string, meta?: object) => void} [deps.onMilestone]
 * @returns {{ ok: true, events: string[] }}
 */
export function executeSeatTapSelect (deps) {
  const { tracker, seat, pushWsSelected, openModal, onMilestone } = deps

  const seatMeta = {
    seatId: seat?.seatId,
    label: seat?.label ?? seat?.rowLabel ?? undefined,
  }
  const note = (name, extra = {}) => {
    emitSeatOpt(name, { ...seatMeta, ...extra })
    if (typeof onMilestone === 'function') onMilestone(name, { ...seatMeta, ...extra })
  }

  const events = []
  tracker.setSeat(seat)
  pushWsSelected()
  events.push('ws_selected')
  note('ws_selected')
  openModal()
  events.push('modal_open')
  note('modal_open')

  return { ok: true, events }
}

/**
 * Confirmed-seat deselect ordering: UI/state first, unblock fire-and-forget.
 * @param {object} deps
 * @param {() => void} deps.applyLocalDeselect - sync UI (setSelected)
 * @param {() => void|Promise<any>} deps.unblock - must be invoked without awaiting for UI-first
 * @returns {string[]} events
 */
export function executeConfirmedDeselect (deps) {
  const events = []
  const { applyLocalDeselect, unblock } = deps
  applyLocalDeselect()
  events.push('ui_deselected')
  void Promise.resolve(unblock()).then(() => {
    events.push('unblock_settled')
  })
  events.push('unblock_kicked')
  return events
}

/**
 * Optimistic Continuar: commit selected + close modal before awaiting the block.
 * Block runs in background; hard-fail triggers rollback (no ghost passenger).
 *
 * @param {object} deps
 * @param {() => void} deps.commitUi - setSelected + destroyModal (sync)
 * @param {boolean} deps.needsFareBlock - true if the confirmation block still has to run
 * @param {() => Promise<{ ok: boolean }>} deps.blockFare
 * @param {() => void} deps.rollback - remove selected + release + alert
 * @param {(result: { ok: boolean }) => void} [deps.onSettled]
 * @returns {{ ok: true, events: string[], farePromise: Promise<{ ok: boolean, events: string[] }> }}
 */
export function executeOptimisticContinuar (deps) {
  const { commitUi, needsFareBlock, blockFare, rollback, onSettled } = deps
  const events = []
  commitUi()
  events.push('ui_committed')
  emitSeatOpt('continuar_ui_committed')

  if (!needsFareBlock) {
    events.push('fare_skipped')
    emitSeatOpt('continuar_fare_skipped')
    if (typeof onSettled === 'function') onSettled({ ok: true })
    return {
      ok: true,
      events,
      farePromise: Promise.resolve({ ok: true, events: [...events] }),
    }
  }

  const farePromise = Promise.resolve()
    .then(() => blockFare())
    .then((result) => {
      if (!result?.ok) {
        rollback()
        events.push('rollback')
        emitSeatOpt('continuar_rollback')
        if (typeof onSettled === 'function') onSettled({ ok: false })
        return { ok: false, events: [...events] }
      }
      events.push('fare_ok')
      emitSeatOpt('continuar_fare_ok')
      if (typeof onSettled === 'function') onSettled({ ok: true })
      return { ok: true, events: [...events] }
    })

  events.push('fare_kicked')
  emitSeatOpt('continuar_fare_kicked')
  return { ok: true, events, farePromise }
}
